import { useLayoutEffect, useRef, type ReactNode } from "react";
import { PAGE_CONTENT_HEIGHT } from "./PdfPage";

interface FitToPageContentProps {
  /** Content to render within the fit-to-page container. */
  children: ReactNode;
  /** Maximum height in pixels. Defaults to PAGE_CONTENT_HEIGHT. */
  maxHeight?: number;
  /** Starting font size in pixels. Defaults to 9. */
  baseFontSize?: number;
  /** Minimum font size in pixels before stopping. Defaults to 1. */
  minFontSize?: number;
}

/**
 * Wraps content and dynamically reduces the container font size until it fits
 * within the specified maximum height. Child elements should use relative font
 * sizes (em) or `inherit` to scale proportionally with the container.
 *
 * Uses direct DOM mutation in useLayoutEffect so parent measurers (e.g.
 * PdfPageViewer) see the final dimensions before paint.
 * @param children - Content to render
 * @param maxHeight - Maximum allowed height in pixels
 * @param baseFontSize - Starting font size in pixels
 * @param minFontSize - Minimum font size floor in pixels
 */
export function FitToPageContent({
  children,
  maxHeight = PAGE_CONTENT_HEIGHT,
  baseFontSize = 9,
  minFontSize = 1,
}: FitToPageContentProps) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    // Reset to base size before measuring
    el.style.fontSize = `${baseFontSize}px`;

    if (el.scrollHeight <= maxHeight) {
      return;
    }

    // Shrink font size until content fits or minimum is reached
    let fontSize = baseFontSize;
    while (el.scrollHeight > maxHeight && fontSize > minFontSize) {
      fontSize -= 0.5;
      el.style.fontSize = `${fontSize}px`;
    }
  });

  return (
    <div
      ref={ref}
      style={{
        fontSize: `${baseFontSize}px`,
        maxHeight: `${maxHeight}px`,
        overflow: "hidden",
      }}
    >
      {children}
    </div>
  );
}
